import React, { createContext, useContext, useState, useCallback } from 'react';

interface StepperContextType {
    currentStep: number;
    setCurrentStep: (step: number) => void;
    nextStep: () => void;
    prevStep: () => void;
    isFirstStep: boolean;
    isLastStep: boolean;
    formData: Record<string, any>;
    updateFormData: (stepData: Record<string, any>) => void;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

interface StepperProviderProps {
    children: React.ReactNode;
    totalSteps: number;
}

export const StepperProvider = ({ children, totalSteps }: StepperProviderProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<Record<string, any>>({});

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    const nextStep = useCallback(() => {
        if (!isLastStep) {
            setCurrentStep(prev => prev + 1);
        }
    }, [isLastStep]);

    const prevStep = useCallback(() => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    }, [isFirstStep]);

    const updateFormData = useCallback((stepData: Record<string, any>) => {
        setFormData(prev => ({
            ...prev,
            ...stepData
        }));
    }, []);

    return (
        <StepperContext.Provider value={{
            currentStep,
            setCurrentStep,
            nextStep,
            prevStep,
            isFirstStep,
            isLastStep,
            formData,
            updateFormData
        }}>
            {children}
        </StepperContext.Provider>
    );
};

export const useStepper = () => {
    const context = useContext(StepperContext);
    if (!context) {
        throw new Error('useStepper must be used within a StepperProvider');
    }
    return context;
};
